import { useState } from "react";

import { ACCESS_CATALOG } from "@nebulize/sdk";

import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Grid,
  Input,
  Page,
  Panel,
  Select,
  Stack,
  StatusBadge,
  Toolbar,
} from "@/components/ui";

import { PagedTable } from "../components/PagedTable";
import {
  useChangeActivityRole,
  useGrantRole,
  useRemoveActivityRole,
  type Role,
  type ScopeRef,
} from "../lib/queries";

const ROLES = ACCESS_CATALOG.roles ?? [];
const SCOPES = ACCESS_CATALOG.scopes ?? [];
const RESOURCES = ACCESS_CATALOG.resources ?? [];

interface RoleRow {
  readonly id: string;
  readonly role: string;
  readonly grantable: boolean;
  readonly permissions: number;
}

interface PermissionRow {
  readonly id: string;
  readonly permission: string;
}

interface ResourceRow {
  readonly id: string;
  readonly kind: string;
  readonly owner_scope: string;
  readonly permissions: number;
}

const ROLE_ROWS: RoleRow[] = ROLES.map((one) => ({
  id: one.role,
  role: one.role,
  grantable: one.grantable === true,
  permissions: (one.permissions ?? []).length,
}));

const RESOURCE_ROWS: ResourceRow[] = RESOURCES.map((one) => ({
  id: one.kind,
  kind: one.kind,
  owner_scope: one.owner_scope,
  permissions: (one.permissions ?? []).length,
}));

function permissionsOf(role: string | undefined): PermissionRow[] {
  const found = ROLES.find((one) => one.role === role);
  return (found?.permissions ?? []).map((permission) => ({ id: permission, permission }));
}

/** The generated catalog, rendered rather than copied, plus the calls that change a holding. */
export function Roles() {
  const [opened, setOpened] = useState<string | undefined>(undefined);
  const [subjectId, setSubjectId] = useState("");
  const [role, setRole] = useState("");
  const [scope, setScope] = useState("");
  const [key, setKey] = useState("");
  const [assignmentId, setAssignmentId] = useState("");
  const [activityRole, setActivityRole] = useState("");
  const [confirming, setConfirming] = useState(false);

  const grant = useGrantRole(subjectId === "" ? undefined : subjectId);
  const change = useChangeActivityRole();
  const remove = useRemoveActivityRole();

  const grantable = ROLES.filter((one) => one.grantable === true);
  const ready = subjectId.trim() !== "" && role !== "" && scope !== "";

  return (
    <Page title="Roles" eyebrow="Platform">
      <Panel title="Roles">
        <PagedTable
          rows={ROLE_ROWS}
          pageSize={12}
          caption="Roles"
          density="comfortable"
          emptyHeadline="Nothing declared"
          placeholder="Find a role"
          match={(row, needle) => row.role.toLowerCase().includes(needle)}
          columns={[
            { key: "role", header: "Role", emphasis: true, mono: true },
            {
              key: "grantable",
              header: "Kind",
              render: (row: RoleRow) => (
                <StatusBadge tone={row.grantable ? "info" : "neutral"}>
                  {row.grantable ? "grantable" : "derived"}
                </StatusBadge>
              ),
            },
            { key: "permissions", header: "Permissions", numeric: true },
            {
              key: "open",
              header: "",
              align: "right",
              width: "140px",
              render: (row: RoleRow) => (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setOpened(row.role);
                  }}
                >
                  Permissions
                </Button>
              ),
            },
          ]}
        />
      </Panel>

      <Panel title="Permissions" subtitle={opened}>
        {opened === undefined ? (
          <EmptyState bare headline="No role opened" />
        ) : (
          <PagedTable
            rows={permissionsOf(opened)}
            pageSize={12}
            caption={`Permissions of ${opened}`}
            emptyHeadline="Grants nothing"
            placeholder="Find a permission"
            match={(row, needle) => row.permission.toLowerCase().includes(needle)}
            columns={[{ key: "permission", header: "Permission", emphasis: true, mono: true }]}
          />
        )}
      </Panel>

      <Panel title="Grant">
        <Stack gap={5}>
          <Grid columns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} align="end">
            <Field label="Subject id">
              <Input
                mono
                value={subjectId}
                onChange={(event) => {
                  setSubjectId(event.target.value);
                }}
              />
            </Field>
            <Field label="Role" required>
              <Select
                value={role}
                options={[
                  { value: "", label: "" },
                  ...grantable.map((one) => ({ value: one.role, label: one.role })),
                ]}
                onChange={(event) => {
                  setRole(event.target.value);
                }}
              />
            </Field>
            <Field label="Scope" required>
              <Select
                value={scope}
                options={[
                  { value: "", label: "" },
                  ...SCOPES.map((one) => ({ value: one, label: one })),
                ]}
                onChange={(event) => {
                  setScope(event.target.value);
                }}
              />
            </Field>
            <Field label="Key">
              <Input
                mono
                disabled={scope === "platform" || scope === ""}
                value={key}
                onChange={(event) => {
                  setKey(event.target.value);
                }}
              />
            </Field>
          </Grid>
          <Toolbar>
            <Button
              variant="primary"
              disabled={!ready}
              loading={grant.isPending}
              onClick={() => {
                const ref: ScopeRef =
                  scope === "platform"
                    ? { scope: "platform" }
                    : { scope: scope as ScopeRef["scope"], key };
                grant.mutate({ subject_id: subjectId.trim(), role: role as Role, scope: ref });
              }}
            >
              Grant
            </Button>
          </Toolbar>
          {grant.isError ? <ErrorState headline={grant.error.message} /> : null}
        </Stack>
      </Panel>

      <Panel title="Activity role assignments">
        <Stack gap={5}>
          <Grid columns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} align="end">
            <Field label="Assignment id">
              <Input
                mono
                value={assignmentId}
                onChange={(event) => {
                  setAssignmentId(event.target.value);
                }}
              />
            </Field>
            <Field label="Role">
              <Input
                mono
                value={activityRole}
                onChange={(event) => {
                  setActivityRole(event.target.value);
                }}
              />
            </Field>
          </Grid>
          <Toolbar>
            <Button
              variant="secondary"
              disabled={assignmentId.trim() === "" || activityRole.trim() === ""}
              loading={change.isPending}
              onClick={() => {
                change.mutate({ assignmentId: assignmentId.trim(), role: activityRole.trim() });
              }}
            >
              Change
            </Button>
            <Button
              variant="destructive"
              disabled={assignmentId.trim() === ""}
              loading={remove.isPending}
              onClick={() => {
                setConfirming(true);
              }}
            >
              Remove
            </Button>
          </Toolbar>
          {change.isError ? <ErrorState headline={change.error.message} /> : null}
          {remove.isError ? <ErrorState headline={remove.error.message} /> : null}
        </Stack>
      </Panel>

      <Panel title="Resource kinds" tone="sunken">
        <PagedTable
          rows={RESOURCE_ROWS}
          pageSize={12}
          caption="Resource kinds"
          emptyHeadline="Nothing declared"
          placeholder="Find a resource kind"
          match={(row, needle) => `${row.kind} ${row.owner_scope}`.toLowerCase().includes(needle)}
          columns={[
            { key: "kind", header: "Kind", emphasis: true, mono: true },
            { key: "owner_scope", header: "Owner scope" },
            { key: "permissions", header: "Permissions", numeric: true },
          ]}
        />
      </Panel>

      <ConfirmDialog
        open={confirming}
        title="Remove the assignment"
        description={assignmentId}
        confirmLabel="Remove"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(assignmentId.trim());
          setConfirming(false);
        }}
        onCancel={() => {
          setConfirming(false);
        }}
      />
    </Page>
  );
}
